import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  CHECKOUT_PROCESS_SUCCESS,
} from 'actions/actionConstants'
import {
  FETCH_CAR_HIRE_OFFERS,
  FETCH_CAR_HIRE_OFFER_IMPORTANT_INFORMATION,
  FETCH_CAR_HIRE_OFFER_INSURANCE_QUOTE,
  FETCH_CAR_HIRE_RESERVATION,
  FETCH_CAR_HIRE_RESERVATION_INSURANCE_INFO,
  FETCH_CAR_HIRE_DESTINATION_PRICE_INFO,
  FETCH_CAR_HIRE_CHEAPEST_OFFER,
  FETCH_CAR_HIRE_BUNDLE_ELIGIBILITY_ORDER,
  FETCH_CAR_HIRE_BUNDLE_ELIGIBILITY_CART,
}
  from 'actions/apiActionConstants'

export const initialCarHireState: App.CarHireState = {
  offerLists: {},
  reservationInfo: {},
  offerImportantInformationLists: {},
  offerInsuranceQuoteLists: {},
  reservationInsuranceInfo: {},
  destinationsPriceInfoList: {},
  cheapestOfferLists: {},
  bundleEligibilityOrdersList: {},
  bundleEligibilityCartList: {},
}

const apiCalls = apiReducerSwitch<App.CarHireState>({
  [FETCH_CAR_HIRE_OFFERS]: {
    request: (state, action) => ({
      offerLists: {
        ...state.offerLists,
        [action.key]: {
          pickUp: { date: '', dateChanged: false },
          dropOff: { date: '', dateChanged: false },
          vehicles: [],
          fetching: true,
          timestamp: undefined,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      offerLists: {
        ...state.offerLists,
        [action.key]: {
          ...action.data,
          fetching: false,
          error: undefined,
          timestamp: Date.now(),
        },
      },
      fetchingAvailableVehicles: false,
    }),
    failure: (state, action) => ({
      offerLists: {
        ...state.offerLists,
        [action.key]: {
          pickUp: { date: '', dateChanged: false },
          dropOff: { date: '', dateChanged: false },
          vehicles: [],
          fetching: false,
          timestamp: (action.error?.status || 500) < 500 ? 0 : Date.now(),
          error: action.error?.message ?? action.error,
        },
      },
    }),
  },
  [FETCH_CAR_HIRE_CHEAPEST_OFFER]: {
    request: (state, action) => ({
      cheapestOfferLists: {
        ...state.cheapestOfferLists,
        [action.key]: {
          offerSummary: undefined,
          fetching: true,
          timestamp: undefined,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      cheapestOfferLists: {
        ...state.cheapestOfferLists,
        [action.key]: {
          offerSummary: action.data,
          fetching: false,
          error: undefined,
          timestamp: Date.now(),
        },
      },
    }),
    failure: (state, action) => ({
      cheapestOfferLists: {
        ...state.cheapestOfferLists,
        [action.key]: {
          offerSummary: undefined,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_CAR_HIRE_OFFER_IMPORTANT_INFORMATION]: {
    request: (state, action) => ({
      offerImportantInformationLists: {
        ...state.offerImportantInformationLists,
        [action.key]: {
          data: [],
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      offerImportantInformationLists: {
        ...state.offerImportantInformationLists,
        [action.key]: {
          data: action.data,
          fetching: false,
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      offerImportantInformationLists: {
        ...state.offerImportantInformationLists,
        [action.key]: {
          data: [],
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_CAR_HIRE_OFFER_INSURANCE_QUOTE]: {
    request: (state, action) => ({
      offerInsuranceQuoteLists: {
        ...state.offerInsuranceQuoteLists,
        [action.key]: {
          data: undefined,
          fetching: true,
          timestamp: undefined,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      offerInsuranceQuoteLists: {
        ...state.offerInsuranceQuoteLists,
        [action.key]: {
          data: action.data,
          fetching: false,
          error: undefined,
          timestamp: Date.now(),
        },
      },
    }),
    failure: (state, action) => ({
      offerInsuranceQuoteLists: {
        ...state.offerInsuranceQuoteLists,
        [action.key]: {
          data: undefined,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_CAR_HIRE_RESERVATION]: {
    request: (state, action) => ({
      reservationInfo: {
        ...state.reservationInfo,
        [action.key]: {
          reservation: undefined,
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      reservationInfo: {
        ...state.reservationInfo,
        [action.key]: {
          reservation: action.data,
          fetching: false,
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      reservationInfo: {
        ...state.reservationInfo,
        [action.key]: {
          reservation: undefined,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_CAR_HIRE_RESERVATION_INSURANCE_INFO]: {
    request: (state, action) => ({
      reservationInsuranceInfo: {
        ...state.reservationInsuranceInfo,
        [action.key]: {
          data: undefined,
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      reservationInsuranceInfo: {
        ...state.reservationInsuranceInfo,
        [action.key]: {
          data: action.data,
          fetching: false,
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      reservationInsuranceInfo: {
        ...state.reservationInsuranceInfo,
        [action.key]: {
          data: null,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_CAR_HIRE_DESTINATION_PRICE_INFO]: {
    request: (state, action) => ({
      destinationsPriceInfoList: {
        ...state.destinationsPriceInfoList,
        [action.key]: {
          destinations: [],
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      destinationsPriceInfoList: {
        ...state.destinationsPriceInfoList,
        [action.key]: {
          destinations: action.data,
          fetching: false,
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      destinationsPriceInfoList: {
        ...state.destinationsPriceInfoList,
        [action.key]: {
          destinations: [],
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_CAR_HIRE_BUNDLE_ELIGIBILITY_ORDER]: {
    request: (state, action) => ({
      bundleEligibilityOrdersList: {
        ...state.bundleEligibilityOrdersList,
        [action.key]: {
          isEligible: false,
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      bundleEligibilityOrdersList: {
        ...state.bundleEligibilityOrdersList,
        [action.key]: {
          ...action.data,
          fetching: false,
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      bundleEligibilityOrdersList: {
        ...state.bundleEligibilityOrdersList,
        [action.key]: {
          isEligible: false,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_CAR_HIRE_BUNDLE_ELIGIBILITY_CART]: {
    request: (state, action) => ({
      bundleEligibilityCartList: {
        ...state.bundleEligibilityCartList,
        [action.key]: {
          isEligible: false,
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      bundleEligibilityCartList: {
        ...state.bundleEligibilityCartList,
        [action.key]: {
          ...action.data,
          fetching: false,
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      bundleEligibilityCartList: {
        ...state.bundleEligibilityCartList,
        [action.key]: {
          isEligible: false,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
})

export default createAppReducer<App.CarHireState>(initialCarHireState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [CHECKOUT_PROCESS_SUCCESS]: () => ({
    offerLists: {},
    reservationInsuranceInfo: {},
    reservationInfo: {},
    offerInsuranceQuoteLists: {},
  }),
})
